import * as Sentry from "@sentry/react";
import { AIError } from "../errors/sentryErrors";
import { API_URL, getAuthHeaders, getAuthOptions } from "./api";

/**
 * Creates a new cost estimate (or fetches an existing cached one) for a given trip.
 * POST /api/cost-estimation/:tripId
 */
export const createCostEstimate = async (tripId) => {
  // console.log(`[costEstimationApi] Requesting createCostEstimate for tripId: ${tripId}`);
  try {
    const response = await fetch(`${API_URL}/api/cost-estimation/${tripId}?type=cost-estimation`, {
      method: "POST",
      headers: getAuthHeaders(),
      signal: AbortSignal.timeout(35000),
      ...getAuthOptions(),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // console.error(`[costEstimationApi] Error creating estimate:`, errorData);
      const err = new Error(errorData.error || "Failed to create cost estimate");
      err.status = response.status;
      err.details = errorData;
      throw err;
    }
    
    const data = await response.json();
    // console.log(`[costEstimationApi] Response from createCostEstimate:`, data);
    return data;
  } catch (error) {
    // console.error(`[costEstimationApi] Exception in createCostEstimate:`, error);
    throw error;
  }
};

/**
 * Forces a refresh of the cost estimate, bypassing the cache. Rate limited to 1/hr.
 * POST /api/cost-estimation/:tripId/refresh
 */
export const refreshCostEstimate = async (tripId) => {
  // console.log(`[costEstimationApi] Requesting refreshCostEstimate for tripId: ${tripId}`);
  try {
    const response = await fetch(`${API_URL}/api/cost-estimation/${tripId}/refresh?type=cost-estimation-refresh`, {
      method: "POST",
      headers: getAuthHeaders(),
      signal: AbortSignal.timeout(35000),
      ...getAuthOptions(),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`[costEstimationApi] Error refreshing estimate:`, errorData);
      const err = new Error(errorData.error || "Failed to refresh cost estimate");
      err.status = response.status;
      err.details = errorData;
      throw err;
    }
    
    const data = await response.json();
    // console.log(`[costEstimationApi] Response from refreshCostEstimate:`, data);
    return data;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "ai");
      scope.setTag("app", "web");
      scope.setTag("error_source", "server");
      scope.setContext("estimate", { trip_id: tripId });
      Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
    });
    console.error(`[costEstimationApi] Exception in refreshCostEstimate:`, error);
    throw error;
  }
};
